import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArcLayer, IconLayer, TextLayer } from 'deck.gl/typed';
import { ColorizedRouteFragment } from 'app/helpers/getColorizedRoute';
import { getColorizedRoute, svgToDataURL } from 'app/helpers';
import { getRouteArrowIcon, getVesselIcon } from 'helpers/getWebGLPointIcon';
import { setHoveredRoutePoint, setSelectedTarget } from 'app/utils/selectedTargetsSlice';
import { setHoveredTarget, setIsVesselPLaybackActive } from 'app/utils/mapStateSlice';
import { useAlertHook } from 'app/hooks/useAlertHook';
import { useAppSelector, useAppDispatch } from 'app/store/store';
import { useLazyGetVesselRouteQuery } from 'app/api/target';
import { TargetPathPositionData, ViewportData } from 'ais-facade-api/api';

import moment from 'moment';

export const useLayerPlaybackVessel = (viewState: any, goToArea: (areasBounds: number[], zoom?: number) => void) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();

  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const isVesselPlaybackActive = useAppSelector((state) => state.mapStateSlice.isVesselPlaybackActive);
  const isPlaybackAvailable = useAppSelector((state) => state.userSlice.uiConfig?.feSinglePlaybackAvailable);

  const isNotBaseMode = mode === 'analytics' || mode === 'playback' || mode === 'history';

  const dispatch = useAppDispatch();
  const selectedTarget = useAppSelector((state) => state.selectedTargetsSlice?.selectedTarget);
  const targetObject = selectedTarget?.target?.object;
  const [getRoute, { ...routeState }] = useLazyGetVesselRouteQuery();
  const isLoading = routeState.isFetching || routeState.isLoading;

  const [pointsArray, setPointsArray] = useState<Array<any>>([]);
  const [startDate, setStartDate] = useState<any>();
  const [point, setPoint] = useState<any>([]);
  const [pointsInTimelineArray, setPointsInTimelineArray] = useState<Array<any>>([]);
  const [colorizedRoute, setColorizedRoute] = useState<ColorizedRouteFragment[] | null>(null);

  const zoomToPath = (path: TargetPathPositionData[]) => {
    if (!(path.length > 0)) return;
    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    path.map(({ lon, lat }) => {
      if (typeof lon === 'number' && typeof lat === 'number') {
        minLng = Math.min(minLng, lon);
        maxLng = Math.max(maxLng, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      }
    });
    const bounds = [minLng, minLat, maxLng, maxLat];
    goToArea(bounds);
  };

  const playbackVesselLayer = new IconLayer({
    id: 'layer-playback-vessel',
    data: point,
    pickable: true,
    getIcon: () => ({
      url: svgToDataURL(getVesselIcon(targetObject, false, isVesselPlaybackActive)),
      width: 28,
      height: 28,
    }),
    getPosition: (d: ViewportData): [number, number, number] => {
      if (typeof d?.lon === 'number' && typeof d?.lat === 'number') return [d.lon, d.lat, 0];
      return [0, 0, 10];
    },

    getAngle: (d) => {
      return d.registrySpecialType === 'ATON' ? 0 : -d.heading || -d.cog;
    },

    sizeMinPixels: 28,
    visible: !isLoading && !isNotBaseMode && isVesselPlaybackActive,

    onClick(pickingInfo) {
      dispatch(
        setSelectedTarget({
          target: { ...pickingInfo, object: Object.values(pickingInfo.object)[0] },
          type: pickingInfo.object.registrySpecialType === 'ATON' ? 'aton' : 'vessel',
        }),
      );
    },
    onHover(pickingInfo) {
      if (pickingInfo.object) {
        dispatch(setHoveredTarget({ ...pickingInfo, object: Object.values(pickingInfo?.object)[0] }));
      }
    },
  });

  const playbackRouteLayer = new ArcLayer({
    id: 'playback-route-layer',
    visible: !isLoading && !isNotBaseMode && isVesselPlaybackActive,
    data: colorizedRoute || [],
    getSourcePosition: (d) => [d.from.lon, d.from.lat],
    getSourceColor: (d) => d.inboundColor,
    getTargetColor: (d) => d.outboundColor,
    getTargetPosition: (d) => [d.to.lon, d.to.lat],
    getHeight: 0,

    positionFormat: 'XY',
    widthMinPixels: 1,
    pickable: false,
    wrapLongitude: true,
  });

  const playbackRoutePoinsLayer = new IconLayer({
    id: 'playback-route-points-layer',
    data: colorizedRoute || [],
    pickable: true,
    positionFormat: 'XY',
    getPosition: (d: ColorizedRouteFragment): [number, number, number] => {
      if (typeof d.from?.lon === 'number' && typeof d.from?.lat === 'number') return [d.from.lon, d.from.lat, 0];
      return [0, 0, 10];
    },
    getAngle: (d: ColorizedRouteFragment) => {
      if (typeof d.from?.heading === 'number') return 360 - d.from?.heading;
      if (typeof d.from.cog === 'number') return 360 - d.from?.cog;
      else return 0;
    },
    getIcon: () => ({
      url: svgToDataURL(getRouteArrowIcon()),
      width: 6,
      height: 8,
    }),
    billboard: true,
    sizeMinPixels: 8,
    autoHighlight: false,
    visible: !isLoading && !isNotBaseMode && isVesselPlaybackActive,
    onHover: (point) => {
      dispatch(setHoveredRoutePoint(point));
    },
  });

  const playbackVeseelNameLayer = new TextLayer({
    id: 'playback-layer-vessel-name',
    data: point,
    getPosition: (d: ViewportData): [number, number, number] => {
      if (typeof d?.lon === 'number' && typeof d?.lat === 'number') return [d.lon, d.lat, 0];
      return [999, 999, 0];
    },
    getText: () => {
      return targetObject.registryName ? targetObject.registryName : targetObject.mmsi;
    },
    getColor: [255, 255, 255, 255],
    getSize: 10,
    getAngle: 0,
    getTextAnchor: 'start',
    getAlignmentBaseline: 'bottom',
    visible: !isLoading && !isNotBaseMode && isVesselPlaybackActive && viewState.zoom > 6,
    pickable: false,
  });

  useEffect(() => {
    if (isPlaybackAvailable && isVesselPlaybackActive && selectedTarget && selectedTarget.type === 'vessel') {
      getRoute(targetObject?.mmsi);
    } else {
      dispatch(setIsVesselPLaybackActive(false));
      setColorizedRoute(null);
      setPointsArray([]);
      setPointsInTimelineArray([]);
      setPoint(null);
    }
  }, [selectedTarget, isVesselPlaybackActive]);

  useEffect(() => {
    if (!isLoading && routeState.isSuccess && routeState.data.results) {
      zoomToPath(routeState.data.results);
      const reversedPath = [...routeState.data.results].reverse();
      setPointsArray(reversedPath);
      const firstPointT = reversedPath[0]?.t;
      setStartDate(firstPointT);

      const colorizedData = getColorizedRoute(routeState.data.results);
      if (colorizedData && colorizedData.length > 0 && !isNotBaseMode) {
        setColorizedRoute(colorizedData);
      } else {
        if (colorizedData && colorizedData.length === 0) {
          addAlert({
            data: {
              messages: [{ description: t('NoPlaybackVesselData'), type: 'information' }],
            },
          });
          dispatch(setIsVesselPLaybackActive(false));
        }
        setColorizedRoute(null);
      }
    }
  }, [isLoading, routeState.data, isNotBaseMode]);

  useEffect(() => {
    if (routeState.error) {
      addAlert(routeState.error);
    }
  }, [routeState.error]);

  useEffect(() => {
    if (pointsArray.length >= 1 && startDate) {
      const startTime = moment(startDate).valueOf(); //стартовое время в милисек

      const lastPointT = pointsArray[pointsArray.length - 1].t;

      const fullAnimationDuration = moment(lastPointT).diff(startTime, 'days');
      const fullPathDuration = moment(lastPointT).diff(startTime);

      const timedPositions = pointsArray.map((point) => {
        const diff1 = fullPathDuration / fullAnimationDuration;
        const diff = moment(point.t).diff(startTime) / diff1;
        return {
          time: diff,
          ...point,
        };
      });
      setPointsInTimelineArray(timedPositions);
    }
  }, [pointsArray, startDate]);

  useEffect(() => {
    let intervals: NodeJS.Timeout[] = [];
    if (pointsInTimelineArray.length > 0) {
      intervals = pointsInTimelineArray.map((timeElement) => {
        const { time, ...elementData } = timeElement;

        const interval = setTimeout(() => {
          setPoint([elementData]);
        }, time * 1000);

        return interval;
      });

      return () => {
        intervals?.flat()?.forEach((timer) => clearTimeout(timer));
      };
    }

    return () => {
      intervals?.flat()?.forEach((timer) => clearInterval(timer));
    };
  }, [pointsInTimelineArray]);

  return [playbackVeseelNameLayer, playbackRouteLayer, playbackRoutePoinsLayer, playbackVesselLayer];
};
